import { ComponentProps, forwardRef } from 'react';

import { AudioFileViewer, type AudioFileViewerSize } from './audio-file-viewer';
import { DocViewerLoader } from './doc-viewer-loader';
import { audioVideoFileTypes } from '../../../../../backend/src/matter/doc/allowed-extensions';
import { PDFViewer, PDFViewerRef } from '@/new/components/pdf-viewer';
import { TimeRangeWithId } from '@/types';

type DocumentViewerProps = ComponentProps<typeof PDFViewer> & {
  matterId?: string;
  fileExtension?: string;
  timeHighlights?: TimeRangeWithId[];
  audioSize?: AudioFileViewerSize;
};

export const isAudioFileExtension = (extension?: string) => {
  if (!extension) return false;
  const normalizedExtension = extension.startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`;
  return audioVideoFileTypes.includes(normalizedExtension);
};

export const DocumentViewer = forwardRef<PDFViewerRef, DocumentViewerProps>(
  ({ matterId, fileExtension, timeHighlights, audioSize, ...pdfViewerProps }, ref) => {
    if (!fileExtension) {
      return (
        <div className="flex h-full items-center justify-center">
          <DocViewerLoader />
        </div>
      );
    }

    if (matterId && isAudioFileExtension(fileExtension)) {
      return (
        <AudioFileViewer
          // Playback state (including duration) belongs to one source. Remount on document
          // changes so a cached URL cannot seek using the previous source's metadata and
          // then be reset to zero when the new source initializes.
          key={pdfViewerProps.docId}
          matterId={matterId}
          docId={pdfViewerProps.docId}
          timeHighlights={timeHighlights}
          highlightedFactId={pdfViewerProps.highlightedFactId}
          onHighlightClick={pdfViewerProps.onHighlightClick}
          onClose={pdfViewerProps.closeViewer}
          size={audioSize}
        />
      );
    }

    return <PDFViewer ref={ref} {...pdfViewerProps} />;
  },
);

DocumentViewer.displayName = 'DocumentViewer';
